import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { Timeout } from './../types/globals';

import { useFetchSitePages } from './site-pages-fetch-context/use-fetch-site-pages';
import { type SitePagesItem, type SitePagesPageType, sitePagesPageType } from './types';

type FetchState =
  | {
      type: 'loading';
    }
  | {
      type: 'loading-error';
      error: NonNullable<any>;
    }
  | {
      type: 'ready';
      cursor?: string | null;
      error?: NonNullable<any>;
      items: SitePagesItem[];
    }
  | {
      cursor: string;
      type: 'loading-more';
      items: SitePagesItem[];
    };
type Cache = Record<string, FetchState>;

type ContextValue = {
  cache: Cache;
  expireCache: () => void;
  initialFetch: (pageType: SitePagesPageType, query: string) => void;
  loadMore: (pageType: SitePagesPageType, query: string) => void;
  preload: () => void;
  sitePublished: boolean | null;
};

const expireTime = 60_000;
const pagingLimit = 100;

const getCacheId = (pageType: SitePagesPageType, query: string): string =>
  `${pageType} ${query.trim()}`;

export const SitePagesFetchContext = createContext<ContextValue>({
  cache: {},
  expireCache: () => {},
  initialFetch: () => {},
  loadMore: () => {},
  preload: () => {},
  sitePublished: null,
});

export const SitePagesFetchContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const fetchSitePages = useFetchSitePages();

  const [cache, setCache] = useState<Cache>({});
  const [expireTimeout, setExpireTimeout] = useState<Timeout | undefined>(undefined);
  const [sitePublished, setSitePublished] = useState<boolean | null>(null);

  const expireCache = useCallback(() => {
    setExpireTimeout(oldTimeout => {
      clearTimeout(oldTimeout);
      return setTimeout(() => setCache({}), expireTime);
    });
  }, []);

  const updateState = useCallback(
    (
      pageType: SitePagesPageType,
      query: string,
      fn: (oldState: FetchState | undefined) => FetchState | undefined | false
    ) => {
      setCache(cache => {
        const cacheId = getCacheId(pageType, query);
        const oldState = cache[cacheId];
        const newState = fn(oldState);
        return newState && newState !== oldState ? { ...cache, [cacheId]: newState } : cache;
      });
    },
    []
  );

  const initialFetch = useCallback(
    (pageType: SitePagesPageType, query: string) => {
      const initialState: FetchState = { type: 'loading' };
      clearTimeout(expireTimeout);

      // This might be triggered in a different component render so we can't update state or we'll get "Warning: Cannot update a component while rendering a different component." error
      queueMicrotask(() =>
        updateState(pageType, query, oldState => {
          if (oldState) {
            return;
          }
          fetchSitePages(pageType, { query: query.trim(), paging: { limit: pagingLimit } })
            .then(result => {
              updateState(
                pageType,
                query,
                oldState =>
                  oldState === initialState && {
                    type: 'ready',
                    items: result.items,
                    cursor: result.paging?.cursors?.next,
                  }
              );
              setSitePublished(sitePublished =>
                'isPublished' in result ? result.published : sitePublished
              );
            })
            .catch(error =>
              updateState(
                pageType,
                query,
                oldState =>
                  oldState === initialState && {
                    type: 'loading-error',
                    error,
                  }
              )
            );

          return initialState;
        })
      );
      return initialState;
    },
    [fetchSitePages, updateState]
  );

  const loadMore = useCallback(
    (pageType: SitePagesPageType, query: string) => {
      clearTimeout(expireTimeout);
      updateState(pageType, query, oldState => {
        if (!oldState || oldState.type !== 'ready' || !oldState.cursor) {
          return;
        }
        const loadingState = {
          type: 'loading-more',
          items: oldState.items,
          cursor: oldState.cursor,
        } as const;
        fetchSitePages(pageType, {
          query: query.trim(),
          paging: { limit: pagingLimit, cursor: oldState.cursor },
        })
          .then(result => {
            updateState(
              pageType,
              query,
              oldState =>
                oldState === loadingState && {
                  type: 'ready',
                  items: [...oldState.items, ...result.items],
                  cursor: result.paging.cursors?.next,
                }
            );
            setSitePublished(sitePublished =>
              'isPublished' in result ? result.published : sitePublished
            );
          })
          .catch(error =>
            updateState(
              pageType,
              query,
              oldState =>
                oldState === loadingState && {
                  type: 'ready',
                  error,
                  cursor: oldState.cursor,
                  items: oldState.items,
                }
            )
          );
        return loadingState;
      });
    },
    [fetchSitePages, updateState]
  );

  const preload = useCallback(() => {
    setExpireTimeout(oldTimeout => {
      clearTimeout(oldTimeout);
      return undefined;
    });
    for (const pageType of Object.values(sitePagesPageType)) {
      initialFetch(pageType, '');
    }
  }, [initialFetch]);

  const value = useMemo(
    () => ({
      cache,
      expireCache,
      initialFetch,
      loadMore,
      preload,
      sitePublished,
    }),
    [cache, expireCache, initialFetch, loadMore, preload, sitePublished]
  );

  return <SitePagesFetchContext.Provider children={children} value={value} />;
};

export const useSitePagesFetchContext = (pageType: SitePagesPageType, query: string) => {
  const value = useContext(SitePagesFetchContext);

  const cacheId = getCacheId(pageType, query);
  const fetchState = value.cache[cacheId] || value.initialFetch(pageType, query);

  const loadMore = useMemo(
    () =>
      fetchState && fetchState.type === 'ready' && fetchState.cursor
        ? () => value.loadMore(pageType, query)
        : undefined,
    [fetchState, value.loadMore]
  );
  return { fetchState, loadMore };
};
